import { useState, useEffect } from "react";
import api, { money } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

const money2 = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PaymentDialog({ open, onOpenChange, purpose, amount, label, lease_id, application_id, onSuccess, allowPartial = false, maxAmount = null }) {
  const [busy, setBusy] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState(process.env.REACT_APP_PAYPAL_CLIENT_ID || "");
  const [cardFee, setCardFee] = useState(null);
  const [amt, setAmt] = useState(amount);

  useEffect(() => { setAmt(amount); }, [amount, open]);

  useEffect(() => {
    let active = true;
    api.get("/payments/config")
      .then((r) => {
        if (!active) return;
        if (r.data?.paypal_enabled && r.data?.paypal_client_id) setPaypalClientId(r.data.paypal_client_id);
        if (r.data?.card_fee) setCardFee(r.data.card_fee);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const cap = maxAmount != null ? Number(maxAmount) : null;
  const effAmount = allowPartial ? Number(amt || 0) : amount;
  const amtValid = !allowPartial || (effAmount > 0 && (cap == null || effAmount <= cap + 0.01));

  const ids = {};
  if (lease_id) ids.lease_id = lease_id;
  if (application_id) ids.application_id = application_id;
  if (allowPartial && effAmount > 0) ids.amount = Math.round(effAmount * 100) / 100;

  // Card processing fee is passed through only for rent/deposit/move-in (destination charges).
  const surchargeApplies = ["security_deposit", "monthly_rent", "move_in_invoice"].includes(purpose);
  const surcharge = (cardFee && surchargeApplies && effAmount)
    ? Math.round((effAmount * cardFee.percent / 100 + cardFee.flat) * 100) / 100
    : 0;
  const cardTotal = Math.round((effAmount + surcharge) * 100) / 100;

  const payStripe = async () => {
    if (!amtValid) return;
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout", { purpose, origin_url: window.location.origin, ...ids });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{label || "Complete payment"}</DialogTitle>
          <DialogDescription>Amount due: <span className="font-semibold text-forest">{money(effAmount)}</span></DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {allowPartial && (
            <div data-testid="partial-rent-section" className="rounded-lg border border-line p-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-ink">Amount to pay</label>
                {cap != null && <span className="text-xs text-mute">{money2(cap)} remaining this month</span>}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mute">$</span>
                  <input
                    data-testid="partial-rent-input"
                    type="number" min="1" step="0.01" max={cap != null ? cap : undefined}
                    value={amt}
                    onChange={(e) => setAmt(e.target.value)}
                    className="w-full border border-line rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20"
                  />
                </div>
                {cap != null && (
                  <button type="button" data-testid="pay-full-remaining" onClick={() => setAmt(cap)} className="text-xs border border-line rounded-lg px-3 py-2 hover:bg-bone whitespace-nowrap">Pay full {money2(cap)}</button>
                )}
              </div>
              {!amtValid && <p className="mt-1 text-xs text-red-600">Enter an amount between $1 and {money2(cap)}.</p>}
              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                You can pay your rent in installments across the month. Note: a card/ACH processing fee applies to <span className="font-semibold">each</span> transaction, so paying in multiple payments means you'll pay the fee more than once. PayPal & Venmo have no fee.
              </p>
            </div>
          )}

          {surcharge > 0 && (
            <div data-testid="card-fee-disclosure" className="rounded-lg border border-line bg-stone-50 p-3 text-xs text-mute">
              <div className="flex justify-between text-ink"><span>{money2(effAmount)}</span><span>Rent/amount due</span></div>
              <div className="flex justify-between mt-0.5"><span>+ {money2(surcharge)}</span><span>Card processing fee ({cardFee.percent}% + {money2(cardFee.flat)})</span></div>
              <div className="flex justify-between mt-1 pt-1 border-t border-line font-semibold text-ink"><span>{money2(cardTotal)}</span><span>Total if paying by card</span></div>
              <p className="mt-1.5">Paying by credit card adds a processing fee. <span className="font-medium text-forest">Choose PayPal or Venmo below to avoid this fee</span> — you'll pay just {money2(effAmount)}.</p>
            </div>
          )}

          <button
            data-testid="pay-stripe-btn"
            disabled={busy || !amtValid}
            onClick={payStripe}
            className="w-full bg-forest text-white py-3 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay with Card{surcharge > 0 ? ` · ${money2(cardTotal)}` : ""}
          </button>

          <div className="relative text-center">
            <span className="text-xs text-mute bg-white px-2 relative z-10">or</span>
            <div className="absolute inset-x-0 top-1/2 border-t border-line" />
          </div>

          <div data-testid="paypal-buttons-container">
            {paypalClientId && amtValid ? (
              <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "USD", intent: "capture", "enable-funding": "venmo" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                  disabled={busy || !amtValid}
                  fundingSource={undefined}
                  forceReRender={[effAmount, purpose]}
                  createOrder={async () => {
                    const { data } = await api.post("/payments/paypal/create-order", { purpose, ...ids });
                    return data.order_id;
                  }}
                  onApprove={async (data) => {
                    setBusy(true);
                    try {
                      await api.post(`/payments/paypal/capture/${data.orderID}`);
                      toast.success("Payment successful via PayPal!");
                      onOpenChange(false);
                      onSuccess?.();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "PayPal payment could not be completed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  onError={() => toast.error("PayPal encountered an error. Please try again.")}
                />
              </PayPalScriptProvider>
            ) : (
              <p className="text-xs text-mute text-center">{paypalClientId ? "Enter a valid amount to pay with PayPal/Venmo." : "PayPal is not configured."}</p>
            )}
            {paypalClientId && (
              <p className="mt-2 text-[11px] text-mute text-center">PayPal, Venmo & card supported. Venmo shows when eligible (US, on mobile/PayPal app).</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
